"use client";

import { useQueryClient } from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect } from "react";
import { useAuth } from "@/lib/auth-client";
import { useAppConfig } from "@/lib/config";
import { useApi } from "@/lib/fetch-client";
import { getLoginRedirectPath, isAuthPathname, isSessionInvalidError } from "@llmgateway/shared/auth-session";
import type { paths } from "@/lib/api/v1";
export type UserMe = paths["/user/me"]["get"]["responses"]["200"]["content"]["application/json"];
export interface UseUserOptions {
  redirectTo?: string;
  redirectWhen?: "authenticated" | "unauthenticated";
  initialData?: UserMe | null;
}
let sessionInvalidationInProgress = false;
let sessionInvalidationHandled = false;
export function useUser(options?: UseUserOptions) {
  const posthog = usePostHog();
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const api = useApi();
  const {
    posthogKey
  } = useAppConfig();
  const {
    signOut
  } = useAuth();
  const redirectTo = options?.redirectTo;
  const redirectWhen = options?.redirectWhen;
  const {
    data,
    isLoading,
    error
  } = api.useQuery("get", "/user/me", {}, {
    retry: 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    initialData: options?.initialData ?? undefined
  });
  useEffect(() => {
    if (!data?.user || !posthogKey) {
      return;
    }
    posthog.identify(data.user.id, {
      email: data.user.email,
      name: data.user.name
    });
  }, [data?.user, posthog, posthogKey]);
  useEffect(() => {
    if (data?.user) {
      sessionInvalidationHandled = false;
    }
  }, [data?.user]);
  useEffect(() => {
    if (!error || !isSessionInvalidError(error)) {
      return;
    }
    if (sessionInvalidationInProgress || sessionInvalidationHandled) {
      return;
    }
    sessionInvalidationInProgress = true;
    void signOut().catch(() => undefined).finally(() => {
      queryClient.clear();
      sessionInvalidationHandled = true;
      sessionInvalidationInProgress = false;
      if (redirectWhen === "unauthenticated" && !isAuthPathname(pathname)) {
        router.replace(getLoginRedirectPath(pathname));
      }
    });
  }, [error, pathname, queryClient, redirectWhen, router, signOut]);
  useEffect(() => {
    if (!redirectTo || !redirectWhen) {
      return;
    }
    const hasUser = !!data?.user;
    if (error && isSessionInvalidError(error)) {
      return;
    }
    if (redirectWhen === "authenticated" && hasUser) {
      router.push(redirectTo);
    } else if (redirectWhen === "unauthenticated" && !isLoading && (!hasUser || error)) {
      router.push(redirectTo);
    }
  }, [data?.user, isLoading, error, router, redirectTo, redirectWhen]);
  return {
    user: data?.user ?? null,
    isLoading,
    error,
    data
  };
}
export function useUpdateUser() {
  const queryClient = useQueryClient();
  const api = useApi();
  return api.useMutation("patch", "/user/me", {
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["user"]
      });
      void queryClient.invalidateQueries({
        queryKey: ["session"]
      });
    }
  });
}
export function useDeleteAccount() {
  const api = useApi();
  return api.useMutation("delete", "/user/me");
}